import CardDetails from '../../components/CardDetails';
import isServiceProfileAdded from '../../add/isServiceProfileAdded';
import { UCAAS_ACCORDION } from '../../constants';

/**
 * (3155) The UCaaS services a setup step applies to, one entry per platform
 * logo:
 * - added: the company already has the service (shown in full colour)
 * - available: it is offered on Add Services but not added yet (faded)
 * - anything else is left out entirely
 *
 * Added services come first. Several services share a logo (Direct Routing and Operator Connect are both
 * Teams), so they collapse into one entry that counts as added when any of
 * them is.
 *
 * @param {object[]} existingServices - the dashboard payload's services
 * @param {object[]} availableProfiles - /ServiceProfiles/Available entries
 * @returns {{ key: string, label: string, icon: string, added: boolean }[]}
 */
export default function resolveStepServices(
    existingServices,
    availableProfiles,
) {
    const services = Array.isArray(existingServices) ? existingServices : [];
    const profiles = Array.isArray(availableProfiles) ? availableProfiles : [];
    const byIcon = new Map();

    const collect = (serviceType, platformID, added) => {
        const details = CardDetails(serviceType, platformID);
        if (details?.addType !== UCAAS_ACCORDION || !details.icon) return;

        const entry = byIcon.get(details.icon) || {
            key: details.serviceType,
            icon: details.icon,
            added: [],
            available: [],
        };
        const names = added ? entry.added : entry.available;
        if (!names.includes(details.serviceName)) {
            names.push(details.serviceName);
        }
        byIcon.set(details.icon, entry);
    };

    services.forEach((service) =>
        collect(service?.serviceType, service?.platformID, true),
    );
    profiles
        .filter((profile) => !isServiceProfileAdded(profile, services))
        .forEach((profile) =>
            collect(
                profile?.serviceType,
                profile?.serviceDefinition?.platformID,
                false,
            ),
        );

    // Added services first, so they are never the ones collapsed out of view.
    return [...byIcon.values()]
        .map((entry) => {
            const added = entry.added.length > 0;
            return {
                key: entry.key,
                icon: entry.icon,
                added,
                label: (added ? entry.added : entry.available).join(', '),
            };
        })
        .sort((a, b) => Number(b.added) - Number(a.added));
}
